import { Box, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { CustomCellProps } from 'components/common/grid/';
import { Popup } from 'components/common/popup/Popup';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { FormRendererAPIType } from 'components/common/form';
import { instance } from 'api/api';
import FiltersFormWrapper from 'components/common/grid/cell/burst-filter-cell/FiltersFormWrapper';
import { useMiNavigation } from 'hooks/useNavigation';
import { DashboardElementType } from 'tools/types';
import { useAppSelector } from 'store/hooks';
import { hasInvalidAlertRuleSelection } from 'components/common/grid/cell/burst-filter-cell/form';

interface BurstFilterCellProps extends CustomCellProps {
    elementId: number;
    segmentValueId: number;
    elementType: DashboardElementType;
    externalId: number;
    listId: number;
    isSubscription: string;
    itemId: number;
    hasFilters: boolean;
    multipleRefsLabel?: string;
}

export default function BurstFilterCell({
    elementId,
    segmentValueId,
    elementType,
    externalId,
    listId,
    isSubscription,
    itemId,
    hasFilters,
    multipleRefsLabel,
}: BurstFilterCellProps) {
    const { t } = useBundleTranslation(['components/notification/burst']);
    const conciergeEnabled = useAppSelector((state) => state.auth?.conciergeInfo?.enabled);
    const [open, setOpen] = useState<boolean>(false);

    const navigate = useMiNavigation();

    const handleClick = () => {
        if (isSubscription == 'Y') {
            navigate(`/extreport/${elementId}#show_alert_wizard`);
            return;
        }

        setOpen(true);
    };

    const [form, setForm] = useState<FormRendererAPIType>();
    const [isLoading, setIsLoading] = useState(false);

    const [isDirty, setIsDirty] = useState(false);
    const [aiInsightsSelectionError, setAiInsightsSelectionError] = useState(false);
    const [alertRuleSelectionError, setAlertRuleSelectionError] = useState(false);
    useEffect(() => {
        const subscription = form?.hookFormWatch((value, { name, type }) => {
            if (!name) {
                return;
            }
            setIsDirty(true);
            setAiInsightsSelectionError(false);
            setAlertRuleSelectionError(false);
        });
        return () => subscription?.unsubscribe();
    }, [form?.hookFormWatch]);

    return (
        <>
            <Tooltip title={t('settings_for_this_report')}>
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        '&:hover': { color: 'primary.main' },
                    }}
                    onClick={handleClick}
                >
                    <IconMi icon={'my-settings'} sx={{ fontSize: '16px', mr: 0.5 }} />
                    {t('settings')}
                </Box>
            </Tooltip>
            <Popup
                settings={{
                    noCancel: true,
                    title: t(elementType == 'internal report' ? 'filter.form_desc_report' : 'filter.form_desc'),
                }}
                okDisabled={!isDirty}
                open={open}
                onHide={() => {
                    setOpen(false);
                }}
                onConfirm={() => {
                    if (!form) {
                        return;
                    }
                    const data = form.hookFormGetValues();
                    if (elementType === 'external report' && conciergeEnabled) {
                        if (data.ai_insight_inclusion_mode === 'selected') {
                            const includedSet = data.ai_included_set;
                            if (!Array.isArray(includedSet) || includedSet.length === 0) {
                                setAiInsightsSelectionError(true);
                                return;
                            }
                        }
                    }
                    if (elementType === 'internal report' && hasInvalidAlertRuleSelection(data)) {
                        setAlertRuleSelectionError(true);
                        return;
                    }
                    setIsLoading(true);
                    const formData = new FormData();
                    for (let key in data) {
                        if (key == 'ai_included_set') {
                            if (Array.isArray(data[key])) {
                                data[key].forEach((v) => {
                                    formData.append(key + '[]', v);
                                });
                            }
                        } else {
                            formData.append(key, data[key]);
                        }
                    }
                    instance
                        .post(
                            `/data/notification/distribution-list-item/${elementId}/${segmentValueId}/${externalId}/${listId}/form`,
                            formData,
                        )
                        .then((response) => {
                            setIsLoading(false);
                            if (response?.data?.status == 'OK') {
                                setIsDirty(false);
                                setOpen(false);
                            }
                        });
                }}
            >
                <>
                    {isLoading && <LoadingPlaceholder />}
                    <FiltersFormWrapper
                        setForm={(form) => setForm(form)}
                        elementId={elementId}
                        segmentValueId={segmentValueId}
                        elementType={elementType}
                        externalId={externalId}
                        listId={listId}
                        hasFilters={hasFilters}
                        multipleRefsLabel={multipleRefsLabel}
                        itemId={itemId}
                        aiInsightsSelectionError={aiInsightsSelectionError}
                        alertRuleSelectionError={alertRuleSelectionError}
                    />
                </>
            </Popup>
        </>
    );
}
